import en from "@/assets/i18n/en.json";
import et from "@/assets/i18n/et.json";
import lt from "@/assets/i18n/lt.json";
import lv from "@/assets/i18n/lv.json";
import sv from "@/assets/i18n/sv.json";
import { createI18n } from "vue-i18n";

const messages = { en, et, lv, lt, sv };

type SupportedLocale = keyof typeof messages;

function isSupportedLocale(value?: string | null): value is SupportedLocale {
  return !!value && value in messages;
}

/**
 * `?language=` is added to a link by hand to force that language, so it outranks the account's own
 * language for as long as it stays in the URL. Nothing stores it; drop the parameter and the
 * account language takes over again.
 *
 * The URL is re-read on every call rather than once at startup, because a logged-out visitor to
 * `/?language=lv` boots on `/callback?code=…`: auth0-vue only navigates to the saved `to.fullPath`
 * after it has exchanged the code, so at startup the parameter is not in the URL yet.
 */
export function resolveLocale(
  accountLanguage?: string | null
): SupportedLocale {
  const requestedLanguage = new URLSearchParams(window.location.search).get(
    "language"
  );
  const browserLanguage = navigator.language.split("-")[0];
  return (
    [requestedLanguage, accountLanguage, browserLanguage].find(
      isSupportedLocale
    ) ?? "en"
  );
}

/**
 * Latvian and Lithuanian use the singular noun form for every count ending in 1
 * (1, 21, 31, …) except the teens (11, 111, …); vue-i18n's default rule treats only
 * exactly 1 as singular, so 21 would pick the plural slot. Slot order stays the
 * repo-wide "none | singular | plural" — which means singular slots must interpolate
 * {count} rather than hardcode "1", since counts like 21 also land there.
 */
const balticPluralRule = (choice: number, choicesLength: number): number => {
  if (choice === 0) return 0;
  const singular = choice % 10 === 1 && choice % 100 !== 11;
  return Math.min(singular ? 1 : 2, choicesLength - 1);
};

export const i18n = createI18n({
  locale: resolveLocale(),
  messages,
  fallbackLocale: "en",
  legacy: false,
  pluralRules: { lv: balticPluralRule, lt: balticPluralRule },
});

export function applyAccountLanguage(
  accountLanguage?: string | null
): SupportedLocale {
  const locale = resolveLocale(accountLanguage);
  i18n.global.locale.value = locale;
  return locale;
}
